import type { Node } from "interfaces/schemas";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import * as treeUtils from "utils/treeUtils";

type NodeToUpdate = {
  id: string | null;
  active: boolean;
};

type Prev = {
  active: boolean;
  partiallyVisible: boolean;
};

type Context = {
  prev: Prev;
};

/**
 * Computes the list of nodes to update when toggling a node's active status.
 *
 * This function handles the cascading effects of toggling a node's active/inactive state:
 * - Updates the target node and all its descendants to the new active status
 * - Recalculates and updates all ancestor nodes' active/partially-active states based on their children
 *
 * @param {Object} node - The node being toggled (React-Arborist NodeApi).
 * @param {boolean} active - The new active status to apply to the node and its descendants.
 *
 * @returns {Array<Object>} An array of node update objects (partial nodes), each containing:
 *   - `id` (string): The node's ID (or null if it's the root project)
 *   - `active` (boolean): The new active status for the node
 *
 * @example
 * // When toggling a node inactive, all its children become inactive,
 * // and all ancestors are recalculated (may become partially active if mixed states exist)
 * const updates = computeNodeToggleActiveStatusUpdates(selectedNode, false);
 * // Returns: [
 * //   { id: 'node-1', active: false },
 * //   { id: 'node-1-child-1', active: false },
 * //   { id: 'node-1-child-2', active: false },
 * //   { id: 'parent-node', active: null } // May change based on sibling states
 * // ]
 *
 * @see updateNodeAndDescendantsActiveStatus - Updates the target node and all descendants
 * @see updateAncestorsActiveStatus - Recalculates ancestor active states
 */

export const computeNodeToggleActiveStatusUpdates = (
  node: NodeApi<Node>,
  active: boolean,
  projectId: string,
) => {
  const nodesToUpdate: NodeToUpdate[] = [];
  const { parent } = node;
  updateNodeAndDescendantsActiveStatus(node, active, nodesToUpdate, projectId);
  if (!treeUtils.isRoot(node.data) && parent) {
    updateAncestorsActiveStatus(parent, nodesToUpdate, projectId);
  }
  return nodesToUpdate;
};

const updateNodeAndDescendantsActiveStatus = (
  node: NodeApi<Node>,
  active: boolean,
  nodesToUpdate: NodeToUpdate[],
  projectId: string,
) =>
  treeUtils.walkTree({
    node,
    childAccessor: (n) => (n.children ? n.children : []),
    callback: (n) => {
      const { data: nodeData } = n;
      nodeData.partiallyVisible = false;
      if (nodeData.active !== active) {
        nodeData.active = active;
        if (!treeUtils.isRoot(n.data)) {
          nodesToUpdate.push({
            id: treeUtils.validateNodeId(nodeData.id, projectId),
            active,
          });
        }
      }
    },
  });

export const updateAncestorsActiveStatus = (
  node: NodeApi<Node>,
  nodesToUpdate: NodeToUpdate[],
  projectId: string,
  initContext?: Context,
) =>
  treeUtils.walkNodeApiAncestors<Context>({
    node,
    callback: (n, context) => {
      const { nodes: children } = n.data;

      let anyActive = false;
      let anyInactive = false;
      let anyPartiallyVisible = false;

      // Determine if any children's are active/inactive/partially active
      if (Array.isArray(children)) {
        for (const child of children) {
          if (child.active) {
            anyActive = true;
          } else {
            anyInactive = true;
          }
          if (child.partiallyVisible) {
            anyPartiallyVisible = true;
          }

          if (
            (anyInactive && anyActive) ||
            (anyInactive && anyPartiallyVisible) ||
            (anyActive && anyPartiallyVisible)
          ) {
            break;
          }
        }
      }

      const contextToUse = context ?? initContext;

      // Determine from the previous iteration (this current node's child) if this node should inherit any active/partially active status
      if (contextToUse) {
        const { prev } = contextToUse;
        if (prev?.partiallyVisible) {
          anyPartiallyVisible = true;
        }
        if (prev?.active) {
          anyActive = true;
        }
      }

      // Calculate and set this node's partiallyVisible status
      const partiallyVisible =
        !treeUtils.isLeafNode(n.data) &&
        ((anyInactive && anyActive) ||
          (anyInactive && anyPartiallyVisible) ||
          (anyActive && anyPartiallyVisible));

      n.data.partiallyVisible = partiallyVisible;

      // Update this node's active status if it has changed
      if (n.data.active !== anyActive) {
        n.data.active = anyActive;
        if (!treeUtils.isRoot(n.data)) {
          nodesToUpdate.push({
            id: treeUtils.validateNodeId(n.data.id, projectId),
            active: anyActive,
          });
        }
      }

      return { prev: { active: anyActive, partiallyVisible } };
    },
  });
